import * as React from 'react';
import classNames from 'classnames';
import initCarousel from '../carousel/index';
import { TinySliderInstance, TinySliderInfo } from 'tiny-slider/src/tiny-slider';
import { CarouselSkipLink } from '../../CarouselSkipLink';

interface ICarouselProps {
    itemsLoaded: boolean;
    itemCount: number;
    children: React.ReactElement[];
    name: string;
    nextFocus: string;
    onIndexChanged?: () => void;
}

interface ICarouselState {
    index: number;
    slideBy: number;
    slideCount: number;
}

const SLIDE_GUTTER_WIDTH = 12;

class Carousel extends React.Component<ICarouselProps, ICarouselState> {
    constructor(props: any) {
        super(props);

        this.state = {
            index: 0,
            slideBy: 0,
            slideCount: 0,
        };

        this.handleIndexChanged = this.handleIndexChanged.bind(this);
        this.initTinySlider = this.initTinySlider.bind(this);
    }

    private _tinySlider?: TinySliderInstance;
    private _removeEventHandlers?: () => void;

    private handleIndexChanged(info: TinySliderInfo) {
        this.setState({ index: info.index, slideBy: info.slideBy, slideCount: info.slideCount });
    }

    private makeSentenceCase(name: string): string {
        const sentence = name.toLowerCase().split('-');
        let titleCase = '';
        for (let i = 0; i < sentence.length; i++) {
            sentence[i] = sentence[i][0].charAt(0).toUpperCase() + sentence[i].slice(1);
            titleCase += sentence[i] + ' ';
        }
        return titleCase;
    }

    private initTinySlider(): void {
        const { name, nextFocus, onIndexChanged } = this.props;
        const { tinySlider, removeEventHandlers } = initCarousel({
            nextFocus,
            container: `#${name}-carousel-slides`,
            gutter: SLIDE_GUTTER_WIDTH,
            nextButton: `#${name}-carousel__next`,
            prevButton: `#${name}-carousel__prev`,
            prevFocus: `#pre-${name}-carousel`,
            slideBy: 1,
            items: 1,
            fixedWidth: 200,
            responsive: {
                900: {
                    gutter: 16,
                    fixedWidth: 389,
                },
                600: {
                    items: 3,
                    slideBy: 3,
                },
                0: {
                    center: false,
                },
            },
        });
        this._tinySlider = tinySlider;
        this._removeEventHandlers = removeEventHandlers;

        if (this._tinySlider && onIndexChanged) {
            this._tinySlider.events.on('indexChanged', this.handleIndexChanged);
        }
    }

    public render(): React.ReactNode {
        const { children, name, nextFocus } = this.props;
        const skipText = this.makeSentenceCase(name);

        return (
            <>
                <CarouselSkipLink id={`pre-${name}-carousel`} href={nextFocus} text={`Skip ${skipText} Carousel`} />
                <div className="carousel max-width--xl">
                    <div
                        id={`${name}-carousel-slides`}
                        className={classNames('carousel__slides', {
                            ['carousel-slides__centered']: children.length < 5,
                        })}
                        data-carousel-length={React.Children.count(children)}
                    >
                        {React.Children.map(children, (child: React.ReactChild, index: number) => (
                            <div
                                className="carousel__slide"
                                data-is-current-slide={this.state.index === index ? true : false}
                                key={`${index}-slide`}
                            >
                                {child}
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    }

    public componentDidMount(): void {
        this.initTinySlider();
    }

    public componentWillUnmount(): void {
        if (this._tinySlider) this._tinySlider.destroy();
        if (this._removeEventHandlers) this._removeEventHandlers();
    }
}

export default Carousel;
